import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AuthContext } from '../../../../contexts/AuthContext';
import { APP_SUCCESS_MESSAGES, VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { redirectBasedOnPermission } from '../../../../utils/methods';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Link from '../../../../components/Link';
import Toast from '../../../../components/Toast';
import AuthService from '../../../../services/auth.service';
import { Login } from '../../../../interfaces/auth.interface';
import { Validation } from '../../../../interfaces/common';
import { Modules, NonAuthRoutes } from '../../../../enums';
import { validateLoginForm } from '../validations';

const Form: React.FC = () => {
  const { setLoggedInUser, setToken, setPermissions, setIsSystemAdmin } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<Login>({
    username: '',
    password: '',
  });
  const [validation, setValidation] = useState<Validation>({ validate: true, fields: [] });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFormData((prev) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setFormData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateLoginForm(
        {
          ...formData,
          [name]: event.target.value.trim(),
        },
        name
      );
      setValidation(newValidation);
    },
    [formData.password, formData.username]
  );

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event && event.preventDefault();

      const newValidation = validateLoginForm(formData);
      setValidation(newValidation);

      if (!newValidation.validate) return;

      setLoading(true);

      try {
        const response: any = await AuthService.login({
          username: formData.username.trim(),
          password: formData.password.trim(),
        });

        if (response.status === 'success') {
          const user = response?.user ?? {};
          const isSystemAdmin = response?.is_system_admin ?? false;
          const userPermissions = response?.user?.permissions ?? response?.permissions ?? null;

          setLoggedInUser(user);
          setToken(response.token ?? '');
          setPermissions(userPermissions);
          setIsSystemAdmin(isSystemAdmin);
          setLoading(false);

          const redirectPath = redirectBasedOnPermission(
            isSystemAdmin,
            userPermissions
          );
          const forbiddenPath = Modules.error + NonAuthRoutes.forbidden;
          navigate(redirectPath || forbiddenPath);
        }
      } catch (err: any) {
        setLoading(false);
      }
    },
    [formData, setLoggedInUser, setToken, setPermissions, setIsSystemAdmin, navigate]
  );

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter') handleSubmit();
    },
    [handleSubmit]
  );

  const handleForgotPassword = () => {
    navigate(Modules.auth + NonAuthRoutes.forgotPassword);
  };

  return (
    <form id="formAuthentication" className="mb-5" autoComplete="off">
      <div className="form-floating form-floating-outline mb-5">
        <Input
          type="text"
          id={VALIDATION_FIELD_MAPPING.username}
          name={VALIDATION_FIELD_MAPPING.username}
          error={
            !!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.username)
          }
          errorMessage={
            validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.username)
              ?.message ?? ''
          }
          label={t('login.pageTexts.form.username.label')}
          placeholder={t('login.pageTexts.form.username.placeholder')}
          value={formData.username}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
        />
      </div>
      <div className="mb-5">
        <div className="form-password-toggle">
          <div className="input-group input-group-merge">
            <div className="form-floating form-floating-outline">
              <Input
                type={showPassword ? 'text' : 'password'}
                id={VALIDATION_FIELD_MAPPING.password}
                name={VALIDATION_FIELD_MAPPING.password}
                error={
                  !!validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.password
                  )
                }
                errorMessage={
                  validation.fields.find(
                    (field) => field.field === VALIDATION_FIELD_MAPPING.password
                  )?.message ?? ''
                }
                label={t('login.pageTexts.form.password.label')}
                placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
                value={formData.password}
                onKeyDown={handleKeyDown}
                onBlur={handleInputBlur}
                onChange={handleInputChange}
              />
            </div>
            <span
              className="password-eye-icon input-group-text cursor-pointer"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              <i className={showPassword ? 'ri-eye-line' : 'ri-eye-off-line'}></i>
            </span>
          </div>
        </div>
      </div>
      <div className="mb-5 d-flex justify-content-between mt-5">
        <div className="form-check mt-2"></div>
        <Link
          className="hyperlink float-end mb-1 mt-2"
          text={t('login.pageTexts.form.forgotPassword')}
          onClick={handleForgotPassword}
        />
      </div>
      <Button
        className="btn btn-primary d-grid w-100"
        text={t('login.pageTexts.form.signInButton')}
        disabled={loading}
        loading={loading}
        onClick={handleSubmit}
      />
    </form>
  );
};

export default Form;
